import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import axios from "axios";

const MOBILE_BREAKPOINT = 768;
const API_URL = process.env.REACT_APP_API_URL || "";

// crm-sidebar-v2
export default function Sidebar({ isOpen, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const role = user?.role;
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [modulosConfig, setModulosConfig] = useState({});

  const handleResize = useCallback(() => {
    const mobile = window.innerWidth < MOBILE_BREAKPOINT;
    setIsMobile(mobile);
    if (!mobile && !isOpen) onToggle(true);
  }, [isOpen, onToggle]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (isMobile) onToggle(false);
  }, [location.pathname, isMobile, onToggle]);

  // Cargar configuración de módulos
  useEffect(() => {
    if (role === "ADMINISTRADOR" || role === "CAJERO") {
      const token = localStorage.getItem("token");
      if (token) {
        axios.get(`${API_URL}/api/configuracion/contable`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setModulosConfig(res.data || {});
        }).catch(() => {});
      }
    }
  }, [role]);

  const isActive = (path) => location.pathname === path;

  const navLink = (to, label, icon, disabled = false) => {
    if (disabled) {
      return (
        <span style={{ ...linkStyle, opacity: 0.35, cursor: "not-allowed", pointerEvents: "none" }}>
          <span style={{ marginRight: 10, fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span> {label}
          <span style={{ marginLeft: "auto", fontSize: 9, background: "#333", padding: "2px 6px", borderRadius: 10, color: "#777" }}>Próx.</span>
        </span>
      );
    }
    return (
      <Link to={to} style={{
        ...linkStyle,
        backgroundColor: isActive(to) ? "rgba(255,255,255,0.1)" : "transparent",
        borderLeft: isActive(to) ? "3px solid #7c8aff" : "3px solid transparent",
        fontWeight: isActive(to) ? 600 : 400,
        color: isActive(to) ? "#fff" : "#ccc",
      }}>
        <span style={{ marginRight: 10, fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span> {label}
      </Link>
    );
  };

  const sectionTitle = (title) => (
    <div style={{ marginTop: 20, marginBottom: 6, padding: "0 20px" }}>
      <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", fontWeight: 700, letterSpacing: 1 }}>
        {title}
      </span>
    </div>
  );

  return (
    <>
      {isMobile && isOpen && (
        <div onClick={() => onToggle(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 998 }} />
      )}

      <aside style={{
        width: 240,
        background: "#1a1a2e",
        color: "#e0e0e0",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 999,
        transform: isOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.3s ease",
        overflowY: "auto",
        overflowX: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #2a2a4a",
        }}>
          <img src="/logo-menu-sidebar-ai-leth-blanco.png" alt="Aileth"
            style={{ maxWidth: 120, height: "auto" }}
            onError={(e) => { e.target.onerror = null; e.target.src = "/logo-aileth-navar.png"; }}
          />
          {isMobile && (
            <button onClick={() => onToggle(false)} style={{
              background: "none", border: "none", color: "#999", fontSize: 20, cursor: "pointer",
            }}>✕</button>
          )}
        </div>

        {/* Navegación */}
        <nav style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, paddingBottom: 16, paddingTop: 8 }}>

          {role === "SUPER_ADMIN" ? (
            <>
              {sectionTitle("Administración")}
              {navLink("/admin/empresas", "Gestión Empresas", "🏢")}
            </>
          ) : (
            <>
              {role === "ADMINISTRADOR" && <>
                {sectionTitle("Bienvenido")}
                {navLink("/", "Inicio", "🏠")}
              </>}

              {role === "ADMINISTRADOR" && <>
                {sectionTitle("Vender")}
                {modulosConfig.pos_habilitado && navLink("/pos", "Punto de Venta (POS)", "🖥️")}
                {modulosConfig.arqueo_caja_habilitado && navLink("/pos/arqueo-caja", "Arqueo de Caja", "🧮")}
                {modulosConfig.recibos_sin_factura_habilitado && navLink("/pos/recibos", "Recibos de Venta", "🧾")}
                {navLink("/crear-factura", "Crear Factura", "🧾")}
                {navLink("/facturaslist", "Facturas", "📑")}
                {navLink("/contabilidad/notas-credito", "Nota de Crédito", "📄")}
                {navLink("/contabilidad/guia-remision", "Guía de Remisión", "🚚")}
                {navLink("/clientes", "Clientes", "👥")}
                {navLink("/cotizaciones", "Cotizar", "📋")}

                {sectionTitle("Catálogo")}
                {navLink("/inventario/productos", "Productos", "📦")}
                {navLink("/servicios", "Servicios", "🔧")}
                {navLink("/inventario/bodegas", "Bodegas", "🏭")}
                {navLink("/inventario/ingresos-egresos", "Ingresos/Egresos", "↕️")}
                {navLink("/inventario/transferencias", "Transferencias", "🔄")}
                {navLink("/inventario/kardex", "Kardex", "📒")}
                {navLink("/inventario/importar-csv", "Importar CSV", "📤")}

                {sectionTitle("Compras")}
                {navLink("/compras", "Nueva Compra", "🛒")}
                {navLink("/compras-manual", "Compra Manual", "📝")}
                {navLink("/contabilidad/liquidacion-compras", "Liquidación de Compra", "📋")}
                {navLink("/proveedores", "Proveedores", "🏭")}
                {modulosConfig.retenciones_habilitadas && navLink("/contabilidad/retenciones", "Retenciones", "📋")}

                {sectionTitle("Reportes")}
                {navLink("/contabilidad/reportes", "Reportes Contables", "📊")}
                {navLink("/contabilidad/libro-diario", "Libro Diario", "📖")}
                {navLink("/contabilidad/ats", "ATS (Anexo SRI)", "📋")}

                {sectionTitle("Finanzas")}
                {navLink("/cuentas-cobrar", "Cuentas por Cobrar", "💰")}
                {navLink("/cuentas-pagar", "Cuentas por Pagar", "💸")}
                {modulosConfig.tesoreria_habilitada && navLink("/tesoreria", "Tesorería", "🏦")}

                {sectionTitle("CRM")}
                {navLink("/crm/pipeline", "🔥 Pipeline de Ventas", "📈")}
                {navLink("/crm/bandeja", "Bandeja de Mensajes", "💬")}
                {navLink("/crm/contactos", "Contactos / Leads", "🤝")}

                {sectionTitle("Configuración")}
                {modulosConfig.centro_costos_habilitado && navLink("/contabilidad/centro-costos", "Centro de Costos", "🏗️")}
                {navLink("/configuracion", "Mi Empresa", "🏢")}
                {navLink("/configuracion/usuarios", "Usuarios / Cajeros", "👥")}
                {navLink("/contabilidad/configuracion", "Contabilidad", "⚙️")}
                {navLink("/configuracion/email", "Email - SMTP", "📧")}
                {navLink("/configuracion/whatsapp", "WhatsApp Business", "📱")}
                {navLink("/configuracion/chat-widget", "Chat Widget", "🔌")}
                {navLink("/configuracion/asistente-ia", "Asistente IA", "🤖")}
                {modulosConfig.pos_habilitado && navLink("/pos/impresora", "Impresora / POS", "🖨️")}
              </>}

              {(role === "CAJERO") && <>
                {sectionTitle("Vender")}
                {modulosConfig.pos_habilitado && navLink("/pos", "Punto de Venta (POS)", "🖥️")}
                {modulosConfig.arqueo_caja_habilitado && navLink("/pos/arqueo-caja", "Arqueo de Caja", "🧮")}
                {modulosConfig.recibos_sin_factura_habilitado && navLink("/pos/recibos", "Recibos de Venta", "🧾")}
                {navLink("/crear-factura", "Crear Factura", "📄")}
                {navLink("/facturaslist", "Facturas", "📑")}
                {navLink("/clientes", "Clientes", "👥")}
                {sectionTitle("CRM")}
                {navLink("/crm/bandeja", "Bandeja de Mensajes", "💬")}
                {sectionTitle("Ajustes")}
                {navLink("/pos/impresora", "Tema / Color", "🎨")}
              </>}
            </>
          )}
        </nav>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "1px solid #2a2a4a", flexShrink: 0,
        }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user?.nombre || user?.email}
          </div>
          <button onClick={logout} style={{
            width: "100%", padding: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid #333",
            borderRadius: 6, cursor: "pointer", fontSize: 12, color: "#aaa",
          }}>
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}

const linkStyle = {
  display: "flex",
  alignItems: "center",
  padding: "9px 20px",
  textDecoration: "none",
  color: "#ccc",
  fontSize: 13,
  borderRadius: 0,
  transition: "all 0.15s",
};
